import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const RM_TOKEN_KEY = 'weddingly_rm_token';
const RM_USER_KEY = 'weddingly_rm_user';

@Injectable({ providedIn: 'root' })
export class RmService {
  private apiUrl = `${environment.apiUrl}/rm`;
  private rmUser$ = new BehaviorSubject<any>(this.getStoredRm());

  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    const token = localStorage.getItem(RM_TOKEN_KEY) || '';
    return new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
  }

  // ─── Auth ──────────────────────────────────────────────────────────
  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/login`, { email, password }).pipe(
      tap(res => {
        if (res.status === 'success' && res.data?.token) {
          localStorage.setItem(RM_TOKEN_KEY, res.data.token);
          localStorage.setItem(RM_USER_KEY, JSON.stringify(res.data.rm));
          this.rmUser$.next(res.data.rm);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem(RM_TOKEN_KEY);
    localStorage.removeItem(RM_USER_KEY);
    this.rmUser$.next(null);
  }

  isLoggedIn(): boolean { return !!localStorage.getItem(RM_TOKEN_KEY); }

  getStoredRm(): any {
    try { return JSON.parse(localStorage.getItem(RM_USER_KEY) || 'null'); }
    catch { return null; }
  }

  getRmUser$(): Observable<any> { return this.rmUser$.asObservable(); }

  // ─── Dashboard & Clients ───────────────────────────────────────────
  getDashboardStats(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/dashboard`, { headers: this.headers() });
  }

  getClientDetails(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clients/${clientId}`, { headers: this.headers() });
  }

  // ─── Notes & Tasks ─────────────────────────────────────────────────
  addNote(clientId: string, noteText: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/notes`, { note_text: noteText }, { headers: this.headers() });
  }

  getNotes(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clients/${clientId}/notes`, { headers: this.headers() });
  }

  createTask(clientId: string, data: { title: string; due_date?: string; priority?: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/tasks`, data, { headers: this.headers() });
  }

  updateTask(clientId: string, taskId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/clients/${clientId}/tasks/${taskId}`, data, { headers: this.headers() });
  }

  // ─── Chat & Stage ──────────────────────────────────────────────────
  getChatHistory(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clients/${clientId}/chat`, { headers: this.headers() });
  }

  sendMessage(clientId: string, data: { message_type?: string; content?: string; shared_profiles?: any[] }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/chat`, data, { headers: this.headers() });
  }

  updateStage(clientId: string, stage: string, notes?: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/clients/${clientId}/stage`, { stage, notes }, { headers: this.headers() });
  }

  // ─── Search & Share ────────────────────────────────────────────────
  searchProfiles(filters: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/search`, filters, { headers: this.headers() });
  }

  sendInterestForClient(clientId: string, targetProfileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/send-interest`, { target_profile_id: targetProfileId }, { headers: this.headers() });
  }

  // ─── Recommendations & Candidate Preview ───────────────────────────
  getCandidateProfilePreview(candidateId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/profiles-preview/${candidateId}`, { headers: this.headers() });
  }

  shareRecommendations(clientId: string, profiles: any[], messageContent?: string, rmNote?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/recommendations/share`, { profiles, message_content: messageContent, rm_note: rmNote }, { headers: this.headers() });
  }

  getClientRecommendations(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clients/${clientId}/recommendations`, { headers: this.headers() });
  }

  toggleBookmark(clientId: string, profileId: string, notes?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/clients/${clientId}/bookmarks`, { profile_id: profileId, notes }, { headers: this.headers() });
  }

  getClientBookmarks(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/clients/${clientId}/bookmarks`, { headers: this.headers() });
  }
}
